import { NgIf } from '@angular/common';
import { Component, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter } from '@angular/material-moment-adapter';
import { MatCard } from '@angular/material/card';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatCalendar, MatCalendarCellCssClasses } from '@angular/material/datepicker';
import { MatFormField } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import moment, { Moment } from 'moment';
import { dateToString } from '../../helpers/date-to-string.helper';
import { CustomDynamicFormControlComponent } from '../custom-dynamic-form-control.component';
import { DateRangePickerModel } from './date-range-picker.model';

@Component({
  selector:      'gsm-date-range-picker',
  templateUrl:   './date-range-picker.component.html',
  styleUrls:     ['./date-range-picker.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers:     [
    {
      provide:  DateAdapter,
      useClass: MomentDateAdapter,
      deps:     [MAT_DATE_LOCALE]
    },
    {
      provide:  MAT_DATE_FORMATS,
      useValue: MAT_MOMENT_DATE_FORMATS
    }
  ],
  standalone:    true,
  imports:       [
    MatFormField,
    MatInput,
    FormsModule,
    NgIf,
    MatCard,
    MatCalendar
  ]
})
export class DateRangePickerComponent extends CustomDynamicFormControlComponent<DateRangePickerModel> implements OnInit {

  @ViewChild('calendar', {static: false})
  calendar: MatCalendar<Moment>;

  readonly dateFormat = 'DD.MM.YYYY';

  startInputValue = '';
  endInputValue = '';

  constructor() {
    super();
  }

  /**
   *  set values from model
   */
  protected onModelChange(): void {
    if (!this.model.value) {
      this.model.value = {};
    }

    // set value
    this.start = this.model.value.start || '';
    this.end = this.model.value.end || '';
  }

  get maxDate(): Moment {
    return (this.model.noFuture && moment()) || null;
  }

  get minDate(): Moment {
    return (this.model.noPast && moment()) || null;
  }

  /**
   * Store and maintain model value for start date.
   */
  set start(date: string) {
    if (!date && this.start === this.end) {
      // reset start and end value if they are same end date is reset
      this.model.value.start = '';
      this.model.value.end = '';
    } else {
      this.model.value.start = date;
      this.startInputValue = (this.startDate && this.startDate.format(this.dateFormat)) || date;

      // if end date does not exist yet, but start date is valid,
      // set end date to start date.
      if (!this.end && this.isValidStart) {
        this.model.value.end = this.start;
        this.endInputValue = this.startInputValue;
      }
    }

    this.handleValueChange(this.startDate);
  }

  /**
   * proxy getter for model start value
   */
  get start(): string {
    return this.model.value.start;
  }

  /**
   * convenience getter for start date as moment.
   */
  get startDate(): Moment {
    const date = moment(this.start);
    return date.isValid() ? date : null;
  }

  /**
   * Store and maintain model value for end date.
   */
  set end(date: string) {
    // reset start and end value if they are same end date is reset
    if (!date && this.start === this.end) {
      this.model.value.start = '';
      this.model.value.end = '';
    } else {
      this.model.value.end = date;
      this.endInputValue = (this.endDate && this.endDate.format(this.dateFormat)) || date;

      // if start date does not exist yet, but end date is valid,
      // set start date to end date.
      if (!this.start && this.isValidEnd) {
        this.model.value.start = this.end;
        this.startInputValue = this.endInputValue;
      }
    }

    this.handleValueChange(this.endDate);
  }

  /**
   * proxy getter for model end value
   */
  get end(): string {
    return this.model.value.end;
  }

  /**
   * convenience getter for end date as moment.
   */
  get endDate(): Moment {
    const date = moment(this.end);
    return date.isValid() ? date : null;
  }

  /**
   * in fact, we do not really need a getter for a value,
   * but to make the two-way binding possible, we implement it.
   */
  get pickerValue(): Moment {
    return null;
  }

  /**
   * This setter determines whether the given value should be start or end date
   * and reorganizes dates if necessary.
   */
  set pickerValue(value) {
    // in case an input was not blurred
    const strValue = dateToString(value);

    // start date does not exist
    // :- start (and implicitly end) date is set to value
    if (!this.start) {
      this.start = strValue;

      // value equals start
      // :- if start equals end, reset start (and implicitly end) to null, otherwise reset end to value.
    } else if (value.isSame(this.startDate, 'date')) {
      if (this.end === this.start) {
        this.start = null;
        return;
      }

      this.end = strValue;

      // value equals end
      // :- reset start to value
    } else if (value.isSame(this.endDate, 'date')) {
      this.start = strValue;

      // value is before start date
      // :- if end date exists, leave it alone, otherwise set end date to start date
      // :- set start date to value
    } else if (value.isBefore(this.startDate, 'date')) {
      this.end = this.end || this.start;
      this.start = strValue;

      // value is after start date
      // :- leave start date alone
      // :- set enddate date to value
    } else if (value.isAfter(this.startDate, 'date')) {
      this.end = strValue;
    }
  }

  /**
   * update calendar styles and emit change
   */
  handleValueChange(date: Moment): void {
    if (this.calendar) {
      this.calendar.activeDate = (date && moment(date)) || moment();
      this.calendar.updateTodaysDate();
    }

    this._emitChange();
  }

  private _emitChange(): void {
    // setErrors
    this.group.get(this.model.id)
      .setErrors(this._getErrors());
    this.change.emit(this.model.value);
  }

  private _getErrors() {
    const invalid = !this.isValidStart || !this.isValidEnd;

    return invalid ? {dataRange: true} : null;
  }

  get isValidStart(): boolean {
    return this.isValidDate(this.start);
  }

  get isValidEnd(): boolean {
    return this.isValidDate(this.end);
  }

  isValidDate(date: string): boolean {
    const momentValue = moment(date);
    return (
      // if required: date MUST exist
      (!this.model.required || date) &&
      // if not required: date MAY be empty, either way: existing date MUST be valid
      (!date || // date MUST be parseable by moment.js
        (momentValue.isValid() &&
          // if there is a maximum date, date MUST be <= maximum date
          (!this.maxDate || this.maxDate.isSameOrAfter(momentValue, 'date')) &&
          // if there is a minimum date, date MUST be >= minimum date
          (!this.minDate || this.minDate.isSameOrBefore(momentValue, 'date'))))
    );
  }

  /**
   * handle focus of input events
   */
  handleFocus(date): void {
    if (date) {
      this.calendar.activeDate = date;
    }
  }

  /**
   * handle blur Event for start text input
   */
  handleTextInputBlurStart(): void {
    const val = moment(this.startInputValue, this.dateFormat) || '';
    this.start = val && val.isValid() ? dateToString(val) : '';

    if (!this.isValidStart) {
      this.start = '';
    } else {
      this._swapDatesIfNecessary();
    }
  }

  /**
   * handle blur Event for end text input
   */
  handleTextInputBlurEnd(): void {
    const val = moment(this.endInputValue, this.dateFormat) || '';
    this.end = val && val.isValid() ? dateToString(val) : '';

    if (!this.isValidEnd) {
      this.end = '';
    } else {
      this._swapDatesIfNecessary();
    }
  }

  private _swapDatesIfNecessary(): void {
    if (this.startDate && this.endDate && this.startDate.isAfter(this.endDate)) {
      const realEnd = this.start;
      this.start = this.end;
      this.end = realEnd;
    }
  }

  /**
   *  Function to determine CSS classes for mat-calendar of mat-datepicker to style selection of date range.
   *  In order to achieve theming support,
   *  we use the mat-calendar-body-selected class to set the color.
   */
  get dateClass(): (date: Moment) => MatCalendarCellCssClasses {
    return (date: Moment) => {
      const MAT_SELECTION_CLASS = 'mat-calendar-body-selected';
      const SELECTION_CLASS = 'gsm-date-range-picker__selection';
      const START_CLASS = `${SELECTION_CLASS}--start`;
      const BETWEEN_CLASS = `${SELECTION_CLASS}--between`;
      const END_CLASS = `${SELECTION_CLASS}--end`;

      const retClasses = {};

      // style start or single date
      // if start === end we want the single selection style
      if (this.isValidStart && date.isSame(this.startDate, 'date')) {
        retClasses[MAT_SELECTION_CLASS] = true;
        retClasses[SELECTION_CLASS] = true;
        retClasses[START_CLASS] = this.isValidEnd && !date.isSame(this.endDate, 'date');
        // style end date
      } else if (this.isValidEnd && date.isSame(this.endDate, 'date')) {
        retClasses[MAT_SELECTION_CLASS] = true;
        retClasses[SELECTION_CLASS] = true;
        retClasses[END_CLASS] = true;
        // style dates in range
      } else if (date.isAfter(this.startDate, 'date') && date.isBefore(this.endDate, 'date')) {
        retClasses[MAT_SELECTION_CLASS] = true;
        retClasses[SELECTION_CLASS] = true;
        retClasses[BETWEEN_CLASS] = true;
      }

      return retClasses;
    };
  }
}
